import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoUrl from '@/assets/logo.png';

export interface ReceiptItem {
  description: string;
  sku?: string | null;
  duration_days?: number | null;
  qty: number;
  unit_price_gross: number;
  line_total_gross: number;
}

export interface ReceiptRecord {
  id: string;
  receipt_number: string;
  issued_at: string;
  buyer_name?: string | null;
  buyer_email?: string | null;
  buyer_phone?: string | null;
  buyer_tin?: string | null;
  buyer_address?: string | null;
  seller_name: string;
  seller_tin: string;
  seller_vat_reg?: string | null;
  seller_address: string;
  seller_phone?: string | null;
  seller_email?: string | null;
  items: ReceiptItem[] | unknown;
  currency: string;
  vat_rate: number;
  subtotal_net: number;
  vat_amount: number;
  total_gross: number;
  amount_paid: number;
  payment_method?: string | null;
  payment_reference?: string | null;
  provider_tx_id?: string | null;
  status: string;
  metadata?: Record<string, any> | null;
}

const PRIMARY: [number, number, number] = [34, 83, 138];
const GREY: [number, number, number] = [102, 102, 102];
const LIGHT: [number, number, number] = [241, 245, 251];
const BORDER: [number, number, number] = [216, 221, 230];

const fmt = (n: number) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

let cachedLogo: string | null = null;
async function loadLogoDataUrl(): Promise<string | null> {
  if (cachedLogo) return cachedLogo;
  try {
    const res = await fetch(logoUrl);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => {
        cachedLogo = r.result as string;
        resolve(cachedLogo);
      };
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Build the PDF and return it as a jsPDF instance (ready to .save() or .output()). */
export async function buildReceiptPdf(r: ReceiptRecord): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = 14;

  // Logo
  const logo = await loadLogoDataUrl();
  if (logo) {
    try {
      doc.addImage(logo, 'PNG', margin, y, 22, 22);
    } catch {
      /* ignore */
    }
  }

  // Header text (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...PRIMARY);
  doc.text((r.seller_name || 'ETHIO REAL ESTATES').toUpperCase(), pageW - margin, y + 4, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...GREY);
  const headerLines = [
    r.seller_address,
    [r.seller_email, r.seller_phone].filter(Boolean).join('  •  '),
    `TIN: ${r.seller_tin}${r.seller_vat_reg ? `  •  VAT Reg.: ${r.seller_vat_reg}` : ''}`,
  ].filter(Boolean);
  headerLines.forEach((ln, i) => doc.text(ln!, pageW - margin, y + 10 + i * 4, { align: 'right' }));

  y += 28;
  doc.setDrawColor(...PRIMARY);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // Title + receipt meta
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...PRIMARY);
  doc.text('PAYMENT RECEIPT', margin, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GREY);
  doc.text('Receipt No.', pageW - margin, y - 6, { align: 'right' });
  doc.text('Date Issued', pageW - margin, y + 2, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(r.receipt_number, pageW - margin, y - 2, { align: 'right' });
  doc.text(new Date(r.issued_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }), pageW - margin, y + 6, { align: 'right' });

  y += 12;

  // Billed To / Payment Details boxes
  const boxW = (pageW - margin * 2 - 4) / 2;
  const boxH = 32;

  const drawBox = (x: number, title: string, lines: string[]) => {
    doc.setFillColor(...LIGHT);
    doc.setDrawColor(...BORDER);
    doc.rect(x, y, boxW, 7, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...GREY);
    doc.text(title, x + 3, y + 4.8);

    doc.rect(x, y + 7, boxW, boxH - 7, 'S');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    let ly = y + 12;
    lines.forEach((ln, i) => {
      if (i === 0) doc.setFont('helvetica', 'bold');
      else doc.setFont('helvetica', 'normal');
      doc.text(ln, x + 3, ly);
      ly += 4.5;
    });
  };

  drawBox(margin, 'BILLED TO', [
    r.buyer_name || '—',
    r.buyer_email || '',
    r.buyer_phone || '',
    r.buyer_tin ? `TIN: ${r.buyer_tin}` : '',
  ].filter(Boolean));

  drawBox(margin + boxW + 4, 'PAYMENT DETAILS', [
    `Method: ${(r.payment_method || '—').replace('_', ' ')}`,
    r.provider_tx_id ? `Tx ID: ${r.provider_tx_id}` : '',
    r.payment_reference ? `Reference: ${r.payment_reference}` : '',
    `Status: ${r.status === 'issued' ? 'PAID' : r.status.toUpperCase()}`,
  ].filter(Boolean));

  y += boxH + 8;

  // Items table
  const items: ReceiptItem[] = Array.isArray(r.items) ? (r.items as ReceiptItem[]) : [];
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['#', 'Description', 'Qty', `Unit Price (${r.currency})`, `Amount (${r.currency})`]],
    body: items.map((it, i) => [
      String(i + 1),
      it.description + (it.duration_days ? ` (${it.duration_days} days)` : ''),
      String(it.qty),
      fmt(it.unit_price_gross),
      fmt(it.line_total_gross),
    ]),
    headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: 30 },
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },
      2: { halign: 'center', cellWidth: 14 },
      3: { halign: 'right', cellWidth: 32 },
      4: { halign: 'right', cellWidth: 32 },
    },
    theme: 'grid',
    styles: { lineColor: BORDER, lineWidth: 0.2, cellPadding: 2.5 },
  });

  // @ts-expect-error autotable extends doc
  y = doc.lastAutoTable.finalY + 6;

  // Totals (right aligned)
  const labelX = pageW - margin - 50;
  const valueX = pageW - margin;
  doc.setFontSize(9);
  const row = (label: string, value: string, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(bold ? PRIMARY[0] : 80, bold ? PRIMARY[1] : 80, bold ? PRIMARY[2] : 80);
    doc.text(label, labelX, y);
    doc.text(value, valueX, y, { align: 'right' });
    y += 5;
  };
  row(`Subtotal (excl. VAT)`, `${fmt(r.subtotal_net)} ${r.currency}`);
  row(`VAT (${Number(r.vat_rate).toFixed(0)}%)`, `${fmt(r.vat_amount)} ${r.currency}`);
  doc.setDrawColor(...PRIMARY);
  doc.setLineWidth(0.5);
  doc.line(labelX, y - 2, valueX, y - 2);
  y += 1;
  doc.setFontSize(11);
  row('TOTAL (incl. VAT)', `${fmt(r.total_gross)} ${r.currency}`, true);
  doc.setFontSize(9);
  row('Amount Paid', `${fmt(r.amount_paid)} ${r.currency}`);
  row('Balance Due', `${fmt(Math.max(0, r.total_gross - r.amount_paid))} ${r.currency}`);

  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text('Notes', margin, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GREY);
  const notes =
    'All prices are quoted in ' +
    r.currency +
    ' and are inclusive of ' +
    Number(r.vat_rate).toFixed(0) +
    '% Value Added Tax (VAT). This receipt is computer-generated and is valid without a signature. Purchased credits and package benefits are non-refundable once activated.';
  const split = doc.splitTextToSize(notes, pageW - margin * 2);
  doc.text(split, margin, y);
  y += split.length * 3.5 + 6;

  // Footer
  doc.setDrawColor(...BORDER);
  doc.line(margin, y, pageW - margin, y);
  y += 4;
  doc.setFontSize(7.5);
  doc.text(`${r.seller_name}  •  TIN ${r.seller_tin}  •  ${r.seller_address}`, margin, y);
  doc.text('ethiorealestates.com', pageW - margin, y, { align: 'right' });

  return doc;
}

export async function downloadReceiptPdf(r: ReceiptRecord) {
  const doc = await buildReceiptPdf(r);
  doc.save(`${r.receipt_number}.pdf`);
}

export async function openReceiptPdf(r: ReceiptRecord) {
  const doc = await buildReceiptPdf(r);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
